import { useEffect, useState, useCallback } from "react";
import { useApi } from "./AuthContext";
import { TrendingUp, TrendingDown, ChevronDown, ArrowUpRight, ArrowDownRight, DollarSign, AlertCircle, Wallet, Receipt, FileText, Plus, ChevronRight, Eye, Send, Check, Trash2, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatUSD } from "../utils/currency";
import BottomSheet from "./BottomSheet";

const PERIOD_LABELS = {
  this_month: "This month",
  last_month: "Last month",
  last_30: "Last 30 days",
  this_year: "This year",
};

const INV_STATUS_COLORS = {
  draft: "bg-stone-100 text-stone-700",
  sent: "bg-amber-50 text-amber-700",
  paid: "bg-green-50 text-green-700",
  overdue: "bg-red-50 text-red-700",
};

/**
 * Mobile-only Financial dashboard. Composed view:
 *   - Forest-green hero with period selector + 3 KPI cards
 *   - Cash Flow card (current balance + receivable + overdue)
 *   - Category breakdown (Income / Expenses)
 *   - Quick cards: Receivables / Payables
 *   - Bottom action bar: [Invoices] [+ New] [Budgets]
 */
export default function MobileFinancialDashboard({
  summary,
  invoices,
  expSummary,
  onOpenInvoices,
  onOpenAP,
  onNewInvoice,
  onOpenBudgets,
  onQuickInvoiceAction,
  view, // "dashboard" | "invoices"
  onChangeView,
  onViewInvoice,
}) {
  const api = useApi();
  const [period, setPeriod] = useState("this_month");
  const [categories, setCategories] = useState(null);
  const [loadingCats, setLoadingCats] = useState(true);
  const [showPeriodSheet, setShowPeriodSheet] = useState(false);
  const [actionInv, setActionInv] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoadingCats(true);
    try {
      const { data } = await api.get(`/admin/financial/categories?period=${period}`);
      setCategories(data);
    } catch (err) {
      toast.error("Failed to load category breakdown");
    } finally {
      setLoadingCats(false);
    }
  }, [api, period]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const ChangePill = ({ value, inverse = false }) => {
    if (value === null || value === undefined) return null;
    const isUp = value >= 0;
    const goodWhenUp = !inverse;
    const isGood = (isUp && goodWhenUp) || (!isUp && !goodWhenUp);
    return (
      <span className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${isGood ? "text-green-300" : "text-red-300"}`}>
        {isUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {Math.abs(value)}%
      </span>
    );
  };

  if (view === "invoices") {
    return (
      <>
        <MobileInvoicesView
          invoices={invoices}
          onBack={() => onChangeView("dashboard")}
          onTapInvoice={onViewInvoice}
          onAction={setActionInv}
          onNewInvoice={onNewInvoice}
          onOpenBudgets={onOpenBudgets}
        />
        <InvoiceActionSheet
          invoice={actionInv}
          onClose={() => setActionInv(null)}
          onAction={(action) => {
            onQuickInvoiceAction(actionInv, action);
            setActionInv(null);
          }}
        />
      </>
    );
  }

  return (
    <div className="lg:hidden -m-4 min-h-screen bg-stone-50 pb-24" data-testid="mobile-financial-dashboard">
      {/* Hero */}
      <div className="bg-forest-700 text-white px-5 pt-5 pb-6 rounded-b-3xl shadow-md">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold leading-tight">Financial</h1>
            <p className="text-xs text-forest-200 mt-0.5">Overview of your finances</p>
          </div>
          <button
            data-testid="period-selector-btn"
            onClick={() => setShowPeriodSheet(true)}
            className="inline-flex items-center gap-1.5 bg-forest-600/60 hover:bg-forest-600 px-3 py-1.5 rounded-full text-xs font-medium min-h-[36px]"
          >
            {PERIOD_LABELS[period]}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <KpiCard
            testId="kpi-income"
            label="Income"
            value={categories?.income ?? 0}
            change={categories?.income_pct_change}
            icon={<ArrowUpRight className="w-3.5 h-3.5" />}
            accent="up"
          />
          <KpiCard
            testId="kpi-expenses"
            label="Expenses"
            value={categories?.expenses ?? 0}
            change={categories?.expense_pct_change}
            icon={<ArrowDownRight className="w-3.5 h-3.5" />}
            accent="down"
            inverseGood
          />
          <KpiCard
            testId="kpi-net"
            label="Net"
            value={categories?.net ?? 0}
            change={categories?.net_pct_change}
            icon={<DollarSign className="w-3.5 h-3.5" />}
            accent="neutral"
          />
        </div>
      </div>

      {/* Cash Flow card (current balance) */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm" data-testid="cashflow-card">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Net Cash Flow
          </p>
          <p className="text-2xl font-bold text-stone-900 mt-1" data-testid="cashflow-balance">
            {formatUSD(summary?.net_cash_flow ?? 0)}
          </p>
          <div className="flex gap-2 mt-3">
            <div className="flex-1 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
              <p className="text-[10px] uppercase font-semibold tracking-wide text-green-700">Income (Paid)</p>
              <p className="text-sm font-bold text-green-700 mt-0.5">{formatUSD(summary?.total_paid ?? 0)}</p>
            </div>
            <div className="flex-1 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <p className="text-[10px] uppercase font-semibold tracking-wide text-red-700">Expenses</p>
              <p className="text-sm font-bold text-red-700 mt-0.5">{formatUSD(summary?.total_expenses_paid ?? 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="px-4 mt-4">
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 className="text-sm font-semibold text-stone-800">Summary by Category</h2>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <CategoryCard
            title="Income by Category"
            accent="up"
            loading={loadingCats}
            items={categories?.income_by_category || []}
            testId="income-categories-card"
          />
          <CategoryCard
            title="Expenses by Category"
            accent="down"
            loading={loadingCats}
            items={categories?.expenses_by_category || []}
            testId="expense-categories-card"
          />
        </div>
      </div>

      {/* Quick navigation cards */}
      <div className="px-4 mt-4 space-y-2.5">
        <QuickLinkCard
          testId="quick-receivables"
          icon={<ArrowDownRight className="w-5 h-5 text-green-600" />}
          bgColor="bg-green-50"
          title="Receivables"
          subtitle={`${summary?.overdue_count ?? 0} overdue invoices`}
          value={formatUSD(summary?.total_receivable ?? 0)}
          onClick={() => onOpenInvoices()}
        />
        <QuickLinkCard
          testId="quick-payables"
          icon={<ArrowUpRight className="w-5 h-5 text-red-600" />}
          bgColor="bg-red-50"
          title="Payables"
          subtitle={`${expSummary?.pending_count ?? 0} pending bills`}
          value={formatUSD(summary?.total_expenses_pending ?? 0)}
          onClick={() => onOpenAP()}
        />
        {(summary?.total_overdue ?? 0) > 0 && (
          <QuickLinkCard
            testId="quick-overdue"
            icon={<AlertCircle className="w-5 h-5 text-amber-600" />}
            bgColor="bg-amber-50"
            title="Overdue"
            subtitle={`${summary?.overdue_count ?? 0} invoices need follow-up`}
            value={formatUSD(summary?.total_overdue ?? 0)}
            onClick={() => onOpenInvoices()}
          />
        )}
      </div>

      {/* Bottom action bar — 3-Click Rule */}
      <MobileFinancialActionBar
        active="dashboard"
        onInvoices={onOpenInvoices}
        onNew={onNewInvoice}
        onBudgets={onOpenBudgets}
      />

      {/* Period selector sheet */}
      <BottomSheet
        open={showPeriodSheet}
        onClose={() => setShowPeriodSheet(false)}
        title="Select period"
        testId="period-bottom-sheet"
      >
        <div className="space-y-1">
          {Object.entries(PERIOD_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setPeriod(key); setShowPeriodSheet(false); }}
              data-testid={`period-${key}`}
              className={`w-full flex items-center justify-between px-3 py-3.5 rounded-xl min-h-[48px] text-sm font-medium ${
                period === key ? "bg-forest-50 text-forest-800 border border-forest-200" : "text-stone-700 hover:bg-stone-50"
              }`}
            >
              <span>{label}</span>
              {period === key && <Check className="w-4 h-4 text-forest-600" />}
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Invoice action sheet */}
      <InvoiceActionSheet
        invoice={actionInv}
        onClose={() => setActionInv(null)}
        onAction={(action) => {
          onQuickInvoiceAction(actionInv, action);
          setActionInv(null);
        }}
      />
    </div>
  );
}

function KpiCard({ testId, label, value, change, icon, accent = "neutral", inverseGood = false }) {
  const accentColors = {
    up: "bg-green-500/20 text-green-200",
    down: "bg-red-500/20 text-red-200",
    neutral: "bg-forest-500/20 text-forest-100",
  };
  const isUp = (change ?? 0) >= 0;
  const goodPositive = !inverseGood;
  const isGood = (isUp && goodPositive) || (!isUp && !goodPositive);
  return (
    <div className="bg-forest-600/40 rounded-2xl p-3 border border-forest-500/30" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-medium text-forest-100/90">{label}</p>
        <span className={`w-5 h-5 rounded-full flex items-center justify-center ${accentColors[accent]}`}>
          {icon}
        </span>
      </div>
      <p className="text-base font-bold text-white leading-tight" data-testid={`${testId}-value`}>
        {formatUSD(value)}
      </p>
      {change !== null && change !== undefined && (
        <p className={`text-[10px] font-semibold mt-1 inline-flex items-center gap-0.5 ${isGood ? "text-green-300" : "text-red-300"}`}>
          {isUp ? "↑" : "↓"} {Math.abs(change)}% vs prev
        </p>
      )}
    </div>
  );
}

function CategoryCard({ title, items, loading, accent, testId }) {
  const barColor = accent === "up" ? "bg-green-500" : "bg-red-400";
  const labelColor = accent === "up" ? "text-green-700" : "text-red-700";
  const iconBg = accent === "up" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600";
  const Icon = accent === "up" ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-stone-800">{title}</p>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center ${iconBg}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
      </div>
      {loading ? (
        <p className="text-xs text-stone-400">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-stone-400 italic">No data in this period</p>
      ) : (
        <div className="space-y-3">
          {items.slice(0, 4).map((it, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-stone-700 font-medium truncate flex-1 mr-2">{it.label}</span>
                <span className={`text-sm font-bold ${labelColor}`}>{formatUSD(it.amount)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                  <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(it.percentage, 100)}%` }} />
                </div>
                <span className="text-[10px] font-semibold text-stone-500 w-8 text-right">{it.percentage}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickLinkCard({ testId, icon, bgColor, title, subtitle, value, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex items-center gap-3 active:scale-[0.98] transition-transform min-h-[64px]"
    >
      <span className={`w-11 h-11 rounded-full flex-shrink-0 ${bgColor} flex items-center justify-center`}>
        {icon}
      </span>
      <div className="flex-1 text-left min-w-0">
        <p className="text-sm font-semibold text-stone-800 leading-tight">{title}</p>
        <p className="text-lg font-bold text-stone-900 mt-0.5">{value}</p>
        <p className="text-[11px] text-stone-500 mt-0.5 truncate">{subtitle}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-stone-300 flex-shrink-0" />
    </button>
  );
}

export function MobileFinancialActionBar({ active, onInvoices, onNew, onBudgets }) {
  return (
    <div
      className="lg:hidden fixed left-0 right-0 z-30 bg-white border-t border-stone-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 56px)" }}
      data-testid="financial-action-bar"
    >
      <div className="flex items-stretch justify-around max-w-md mx-auto">
        <button
          onClick={onInvoices}
          data-testid="action-invoices"
          className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[52px] ${
            active === "invoices" ? "text-forest-700 font-bold" : "text-stone-600 active:text-forest-700"
          }`}
        >
          <FileText className="w-5 h-5" />
          <span className="text-[10px] font-medium">Invoices</span>
        </button>
        <button
          onClick={onNew}
          data-testid="action-new-invoice"
          className="-mt-5 flex flex-col items-center justify-center mx-2"
        >
          <span className="w-12 h-12 rounded-full bg-forest-600 text-white flex items-center justify-center shadow-lg active:bg-forest-700">
            <Plus className="w-6 h-6" />
          </span>
          <span className="text-[10px] font-medium text-forest-700 mt-0.5">New</span>
        </button>
        <button
          onClick={onBudgets}
          data-testid="action-budgets"
          className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[52px] ${
            active === "budgets" ? "text-forest-700 font-bold" : "text-stone-600 active:text-forest-700"
          }`}
        >
          <Receipt className="w-5 h-5" />
          <span className="text-[10px] font-medium">Budgets</span>
        </button>
      </div>
    </div>
  );
}

function MobileInvoicesView({ invoices, onBack, onTapInvoice, onAction, onNewInvoice, onOpenBudgets }) {
  const [filter, setFilter] = useState("all");
  const filtered = invoices.filter((inv) => filter === "all" || inv.status === filter);
  return (
    <div className="lg:hidden -m-4 min-h-screen bg-stone-50 pb-32" data-testid="mobile-invoices-view">
      {/* Header */}
      <div className="bg-forest-700 text-white px-5 pt-5 pb-5 rounded-b-3xl shadow-md">
        <button onClick={onBack} className="text-forest-200 text-sm mb-1 inline-flex items-center gap-1">
          ← Back
        </button>
        <h1 className="text-xl font-semibold">Invoices</h1>
        <p className="text-xs text-forest-200 mt-0.5">{filtered.length} invoice{filtered.length !== 1 ? "s" : ""}</p>
        <div className="mt-3 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          {["all", "draft", "sent", "paid", "overdue"].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3.5 py-2 rounded-full text-xs font-semibold min-h-[40px] min-w-[44px] whitespace-nowrap capitalize ${
                filter === s ? "bg-white text-forest-800" : "bg-forest-600/50 text-forest-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="px-4 mt-4 space-y-2.5">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-sm text-stone-400">
            No invoices in this filter
          </div>
        ) : (
          filtered.map((inv) => (
            <div
              key={inv.id}
              data-testid={`mobile-invoice-card-${inv.id}`}
              className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex items-center gap-3 active:bg-stone-50"
            >
              <button onClick={() => onTapInvoice(inv.id)} className="flex-1 flex items-center gap-3 min-w-0 text-left">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-800 truncate">{inv.client_name || "—"}</p>
                  <p className="text-[11px] text-stone-500 mt-0.5">
                    {inv.invoice_number || inv.id?.slice(0, 8)} · {inv.due_date || "no due date"}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${INV_STATUS_COLORS[inv.status] || INV_STATUS_COLORS.draft}`}>
                      {inv.status}
                    </span>
                    <span className="text-base font-bold text-stone-900">{formatUSD(inv.total_amount || 0)}</span>
                  </div>
                </div>
              </button>
              <button
                onClick={() => onAction(inv)}
                data-testid={`invoice-actions-${inv.id}`}
                className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 active:bg-stone-300 flex items-center justify-center text-stone-600 flex-shrink-0"
                title="Quick actions"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          ))
        )}
      </div>

      <MobileFinancialActionBar
        active="invoices"
        onInvoices={onBack}
        onNew={onNewInvoice}
        onBudgets={onOpenBudgets}
      />
    </div>
  );
}

function InvoiceActionSheet({ invoice, onClose, onAction }) {
  if (!invoice) return null;
  return (
    <BottomSheet
      open={!!invoice}
      onClose={onClose}
      title={`${invoice.invoice_number || invoice.id?.slice(0, 8)} · ${invoice.client_name || ""}`}
      testId="invoice-actions-sheet"
    >
      <div className="space-y-1.5 mt-2">
        <p className="text-xs text-stone-500 mb-2">
          {formatUSD(invoice.total_amount || 0)} · <span className="capitalize">{invoice.status}</span>
        </p>
        <ActionRow
          icon={<Eye className="w-5 h-5 text-blue-600" />}
          label="View / Edit"
          onClick={() => onAction("view")}
          testId="sheet-action-view"
        />
        <ActionRow
          icon={<Download className="w-5 h-5 text-stone-700" />}
          label="Export PDF"
          onClick={() => onAction("pdf")}
          testId="sheet-action-pdf"
        />
        {invoice.status !== "paid" && (
          <ActionRow
            icon={<Check className="w-5 h-5 text-green-600" />}
            label="Mark as Paid"
            onClick={() => onAction("mark_paid")}
            testId="sheet-action-mark-paid"
          />
        )}
        {invoice.status === "draft" && (
          <ActionRow
            icon={<Send className="w-5 h-5 text-forest-600" />}
            label="Mark as Sent"
            onClick={() => onAction("mark_sent")}
            testId="sheet-action-mark-sent"
          />
        )}
        <ActionRow
          icon={<Mail className="w-5 h-5 text-amber-600" />}
          label="Email to Client"
          onClick={() => onAction("email")}
          testId="sheet-action-email"
        />
        <ActionRow
          icon={<Trash2 className="w-5 h-5 text-red-600" />}
          label="Delete"
          onClick={() => onAction("delete")}
          testId="sheet-action-delete"
          danger
        />
      </div>
    </BottomSheet>
  );
}

function ActionRow({ icon, label, onClick, testId, danger = false }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-3.5 rounded-xl min-h-[52px] text-left text-sm font-medium transition-colors ${
        danger ? "text-red-700 hover:bg-red-50 active:bg-red-100" : "text-stone-800 hover:bg-stone-50 active:bg-stone-100"
      }`}
    >
      {icon}
      <span className="flex-1">{label}</span>
      <ChevronRight className="w-4 h-4 text-stone-300" />
    </button>
  );
}
